import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, mergeMap, switchMap, tap } from 'rxjs/operators';

import * as PlatformActions from '@platform/platform.actions';
import * as RouterActions from '@platform/router.actions';
import { ToastService } from '@platform/toast/toast.service';

import { Store } from '@ngrx/store';
import { go } from '@platform/router.actions';
import { HttpStatusCodes, HttpStatusCodeType } from '@platform/shared/http/http-status-codes';
import { isErrorProblemDetail, ProblemDetails } from '@platform/shared/http/problem-details';
import { RootState } from '@root/app.state';
import * as CoreActions from './core.actions';
import { uploadCustomerSpaceIsTermsUpdateApi, uploadCustomerSpaceIsTermsUpdateGroup } from './core.actions';
import { CheckUserResponseType } from './customer-space/check-user.types';
import { CustomerSpaceService } from './customer-space/customer-space.service';
import { FavoriteService } from './favorite/favorite.service';
import { MobileUploadService } from './mobile-upload/mobile-upload.service';
import { NotificationService } from './notification/notification.service';
import { SharepointService } from './sharepoint/sharepoint.service';
import { UserService } from './users/user.service';

@Injectable()
export class CoreEffects {
  loadCustomerSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadCustomerSpace),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.loadCustomerSpace(customerSpaceId).pipe(
          mergeMap((dto) => [CoreActions.loadCustomerSpaceSucceeded({ customerSpace: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadCustomerSpaceFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadCustomerSpaces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadCustomerSpaces),
      switchMap(() =>
        this.customerSpaceService.loadCustomerSpaces().pipe(
          mergeMap((dto) => [CoreActions.loadCustomerSpacesSucceeded({ customerSpaces: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadCustomerSpacesFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadCustomerSpaceCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadCustomerSpaceCount),
      switchMap(() =>
        this.customerSpaceService.getCustomerSpaceCount().pipe(
          mergeMap((dto) => [CoreActions.loadCustomerSpaceCountSucceeded({ count: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadCustomerSpaceCountFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadCustomerSpaceAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadCustomerSpaceAdmin),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.loadCustomerSpaceAdmin(customerSpaceId).pipe(
          mergeMap((dto) => [CoreActions.loadCustomerSpaceAdminSucceeded({ customerSpace: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadCustomerSpaceAdminFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  termsUpdateAccept$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.confirmTermsUpdateActions.confirmTermsUpdate),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.acceptTermsAndUpdates(customerSpaceId).pipe(
          tap(() => console.log('terms were updated')),
          mergeMap(() => [
            CoreActions.confirmTermsUpdateApiActions.confirmTermsUpdateSuccess({ customerSpaceId }),
            RouterActions.back(),
            // RouterActions.clearPath(),
            // RouterActions.go({ path: ['customer-spaces', customerSpaceId], extras: { replaceUrl: true } }),
          ]),
        ),
      ),
    ),
  );

  getTermsUpdateForUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.userTermsUpdateActions.getTermsUpdateForUser),
      switchMap(() =>
        this.customerSpaceService.getUserTermsAndUpdates().pipe(
          tap((dto) => console.log('got terms updates', dto)),
          mergeMap((dto) => [CoreActions.userTermsUpdateActions.getTermsUpdateForUserSuccess({ customerSpaceIds: dto })]),
        ),
      ),
    ),
  );

  checkExternalCustomerSpaceAccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.checkExternalUserCustomerSpaceAccessActions.checkExternalCustomerSpaceAccess),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.checkCustomerSpaceUserAccess(customerSpaceId).pipe(
          // tap((dto) => console.log('Log dto', dto)),
          mergeMap((dto: CheckUserResponseType) => [
            CoreActions.checkExternalUserCustomerSpaceAccessApiActions.checkExternalUserStatusSuccess({ customerSpaceId, dto }),
            // RouterActions.back(),
            // RouterActions.clearPath(),
            // RouterActions.go({ path: ['customer-spaces', customerSpaceId], extras: { replaceUrl: true } }),
          ]),
        ),
      ),
    ),
  );

  // navigateCustomerSpaceOnSuccessfulTermsUpdate$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CoreActions.confirmTermsUpdateApiActions.confirmTermsUpdateSuccess),
  //     switchMap(({ customerSpaceId }) => [go({ path: ['home'] })]),
  //   ),
  // );

  createCustomerSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.createCustomerSpace),
      switchMap(({ customerSpace }) =>
        this.customerSpaceService.createCustomerSpace(customerSpace).pipe(
          tap(() => this.toastService.showSuccessToast('customer-spaces.create-customer.success-message')),
          mergeMap((dto) => [
            CoreActions.createCustomerSpaceSucceeded({ customerSpace: dto }),
            RouterActions.clearPath(),
            RouterActions.go({ path: ['customer-spaces', dto.id, 'admin'], extras: { replaceUrl: true } }),
          ]),
          catchError((err: HttpErrorResponse) => {
            if (err.status !== 409) {
              this.toastService.showErrorToast('customer-spaces.create-customer.error-message');
            }
            return [CoreActions.createCustomerSpaceFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  updateCustomerSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.updateCustomerSpace),
      switchMap(({ id, customerSpace }) =>
        this.customerSpaceService.updateCustomerSpace(id, customerSpace).pipe(
          tap(() => this.toastService.showSuccessToast('customer-spaces.update-customer.success-message')),
          mergeMap((dto) => [CoreActions.updateCustomerSpaceSucceeded({ customerSpace: dto })]),
          catchError((err: HttpErrorResponse) => {
            if (err.status !== 409) {
              this.toastService.showErrorToast('customer-spaces.update-customer.error-message');
            }
            return [CoreActions.updateCustomerSpaceFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  deleteCustomerSpace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.deleteCustomerSpace),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.deleteCustomerSpace(customerSpaceId).pipe(
          tap(() => this.toastService.showSuccessToast('customer-spaces.delete-customer.success-message')),
          mergeMap(() => [
            CoreActions.deleteCustomerSpaceSucceeded(),
            RouterActions.go({ path: ['home', 'customer-spaces'], extras: { replaceUrl: true } }),
          ]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('customer-spaces.delete-customer.error-message');
            return [CoreActions.deleteCustomerSpaceFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  removeCustomerSpaceUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.removeCustomerSpaceUser),
      switchMap(({ customerSpaceId, userId }) =>
        this.customerSpaceService.removeCustomerSpaceUser(customerSpaceId, userId).pipe(
          tap(() => this.toastService.showSuccessToast('customer-spaces.user-management.remove-employee.success-message')),
          mergeMap((dto) => [CoreActions.removeCustomerSpaceUserSucceeded({ customerSpace: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('customer-spaces.user-management.remove-employee.error-message');
            return [CoreActions.removeCustomerSpaceUserFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  updateCustomerSpaceUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.updateCustomerSpaceUser),
      switchMap(({ customerSpaceId, updateUser }) =>
        this.customerSpaceService.updateCustomerSpaceUser(customerSpaceId, updateUser).pipe(
          tap(() => this.toastService.showSuccessToast('customer-spaces.user-management.update-employee.success-message')),
          mergeMap((dto) => [CoreActions.updateCustomerSpaceUserSucceeded({ customerSpace: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('customer-spaces.user-management.update-employee.error-message');
            return [CoreActions.updateCustomerSpaceUserFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  loadCustomerSpaceUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadCustomerSpaceUsers),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.loadCustomerSpaceUsers(customerSpaceId).pipe(
          mergeMap((dto) => [CoreActions.loadCustomerSpaceUsersSucceeded({ users: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadCustomerSpaceUsersFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadActiveUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadActiveUsers),
      switchMap(() =>
        this.userService.getActiveUsers().pipe(
          mergeMap((dto) => [CoreActions.loadActiveUsersSucceeded({ users: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadActiveUsersFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadUserOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadUserOverview),
      switchMap(() =>
        this.userService.getUserOverview().pipe(
          mergeMap((dto) => [CoreActions.loadUserOverviewSucceeded({ users: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadUserOverviewFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadUserOrganizationUnits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadUserOrganizationUnits),
      switchMap(() =>
        this.userService.getUserOrganizationUnits().pipe(
          mergeMap((dto) => [CoreActions.loadUserOrganizationUnitsSucceeded({ organizationUnits: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadUserOrganizationUnitsFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.createUser),
      switchMap(({ user, update, customerSpaceId }) =>
        this.userService.createUser(user).pipe(
          tap((dto) => {
            if (customerSpaceId && update) {
              this.customerSpaceService
                .updateCustomerSpaceUser(customerSpaceId, {
                  ...update,
                  userId: dto.id,
                })
                .subscribe((customerSpace) => {
                  this.store$.dispatch(CoreActions.updateCustomerSpaceSucceeded2({ customerSpace }));
                });
            }
          }),
          tap(() => this.toastService.showSuccessToast('users.create-user.success-message')),
          mergeMap((dto) => [CoreActions.createUserSucceeded({ user: dto })]),
          catchError((err: HttpErrorResponse) => {
            if (err.status !== HttpStatusCodes.CONFLICT) {
              const problemDetailsError: ProblemDetails = isErrorProblemDetail(err.error)
                ? err.error
                : {
                    status: err.status as HttpStatusCodeType,
                    title: err.message,
                    detail: err.message,
                    instance: undefined,
                    extensions: {},
                  };
              this.toastService.showErrorToastDetailFromComponent('users.create-user.error-message', undefined, problemDetailsError);
            }
            return [CoreActions.createUserFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  loadUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadUserProfile),
      switchMap(({ userId }) =>
        this.userService.getUserProfile(userId).pipe(
          mergeMap((dto) => [CoreActions.loadUserProfileSucceeded({ userProfile: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadUserProfileFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  updateUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.updateUserProfile),
      switchMap(({ userId, userProfile }) =>
        this.userService.putUserProfile(userId, userProfile).pipe(
          tap(() => this.toastService.showSuccessToast('users.user-profile.success-message')),
          mergeMap((dto) => [CoreActions.updateUserProfileSucceeded({ userProfile: dto })]),
          catchError((err: HttpErrorResponse) => {
            if (err.status !== HttpStatusCodes.CONFLICT) {
              this.toastService.showErrorToast('users.user-profile.error-message');
            }
            return [CoreActions.updateUserProfileFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  updateUserProfileSuceeded$ = createEffect(
    () => () =>
      this.actions$.pipe(
        ofType(CoreActions.updateUserProfileSucceeded),
        mergeMap(() => [PlatformActions.loadCurrentUser()]),
      ),
  );

  addUserDeputy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.addUserDeputy),
      switchMap(({ userId, deputyId }) =>
        this.userService.addUserDeputy(userId, deputyId).pipe(
          tap(() => this.toastService.showSuccessToast('users.user-profile.add-deputy.success-message')),
          mergeMap((dto) => [CoreActions.addUserDeputySucceeded({ userProfile: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('users.user-profile.add-deputy.error-message');
            return [CoreActions.addUserDeputyFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  removeUserDeputy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.removeUserDeputy),
      switchMap(({ userId, deputyId }) =>
        this.userService.removeUserDeputy(userId, deputyId).pipe(
          tap(() => this.toastService.showSuccessToast('users.user-profile.remove-deputy.success-message')),
          mergeMap((dto) => [CoreActions.removeUserDeputySucceeded({ userProfile: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('users.user-profile.remove-deputy.error-message');
            return [CoreActions.removeUserDeputyFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  resendWelcomeMail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.resendWelcomeMail),
      switchMap(({ userId }) =>
        this.userService.resendWelcomeMail(userId).pipe(
          tap(() => this.toastService.showSuccessToast('users.user-profile.authentication.resend-welcome-mail.success-message')),
          mergeMap((dto) => [CoreActions.resendWelcomeMailSucceeded({ userProfile: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('users.user-profile.authentication.resend-welcome-mail.error-message');
            return [CoreActions.resendWelcomeMailFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  extractSharepointCustomerSpaceTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.extractSharepointCustomerSpaceTemplate),
      switchMap(() =>
        this.sharepointService.extractSharepointCustomerSpaceTemplate().pipe(
          tap(() => this.toastService.showSuccessToast('admin.sharepoint-templates.success-message')),
          mergeMap(() => [CoreActions.extractSharepointCustomerSpaceTemplateSucceeded()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('admin.sharepoint-templates.error-message');
            return [CoreActions.extractSharepointCustomerSpaceTemplateFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  extractSharepointProjectTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.extractSharepointProjectTemplate),
      switchMap(() =>
        this.sharepointService.extractSharepointProjectTemplate().pipe(
          tap(() => this.toastService.showSuccessToast('admin.sharepoint-templates.success-message')),
          mergeMap(() => [CoreActions.extractSharepointProjectTemplateSucceeded()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('admin.sharepoint-templates.error-message');
            return [CoreActions.extractSharepointProjectTemplateFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  getLatestTemplateActualisations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.getLatestTemplateActualisations),
      switchMap(() =>
        this.sharepointService.getLatestActualisedTemplates().pipe(
          mergeMap((dto) => [CoreActions.getLatestTemplateActualisationsSucceeded({ templateActualisation: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.getLatestTemplateActualisationsFailed({ status: err.status, errors: err.error }),
          ]),
        ),
      ),
    ),
  );

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadContacts),
      switchMap(({ customerSpaceId }) =>
        this.customerSpaceService.loadCustomerSpaceContacts(customerSpaceId).pipe(
          mergeMap((dto) => [CoreActions.loadContactsSucceeded({ contacts: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadContactsFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  getCustomerSpaceNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.getCustomerSpaceNotifications),
      switchMap(({ customerSpaceId }) =>
        this.notificationService.getCustomerSpaceNotifications(customerSpaceId).pipe(
          mergeMap((dto) => [CoreActions.getCustomerSpaceNotificationsSucceeded({ notifications: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.getCustomerSpaceNotificationsFailed({ status: err.status, errors: err.error }),
          ]),
        ),
      ),
    ),
  );

  markNotificationAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.markNotificationAsRead),
      switchMap(({ customerSpaceId, notificationId }) =>
        this.notificationService.markNotificationAsRead(customerSpaceId, notificationId).pipe(
          mergeMap((dto) => [CoreActions.markNotificationAsReadSucceeded({ notifications: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.markNotificationAsReadFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  markNotificationAsUnread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.markNotificationAsUnread),
      switchMap(({ customerSpaceId, notificationId }) =>
        this.notificationService.markNotificationAsUnread(customerSpaceId, notificationId).pipe(
          mergeMap((dto) => [CoreActions.markNotificationAsUnreadSucceeded({ notifications: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.markNotificationAsUnreadFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  markAllNotificationsAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.markAllNotificationsAsRead),
      switchMap(({ customerSpaceId }) =>
        this.notificationService.markAllNotificationsAsRead(customerSpaceId).pipe(
          mergeMap((dto) => [CoreActions.markAllNotificationsAsReadSucceeded({ notifications: dto })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.markAllNotificationsAsReadFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  addCustomerSpaceFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.addCustomerSpaceFavorite),
      switchMap(({ customerSpaceId }) =>
        this.favoriteService.addFavorite(customerSpaceId).pipe(
          mergeMap(() => [CoreActions.addCustomerSpaceFavoriteSucceeded({ customerSpaceId })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('fatal-error.text');
            return [CoreActions.addCustomerSpaceFavoriteFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  addFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.addFavorite),
      switchMap(({ parentId }) =>
        this.favoriteService.addFavorite(parentId).pipe(
          mergeMap(() => [CoreActions.addFavoriteSucceeded()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('fatal-error.text');
            return [CoreActions.addFavoriteFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  deleteCustomerSpaceFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.deleteCustomerSpaceFavorite),
      switchMap(({ customerSpaceId }) =>
        this.favoriteService.deleteFavorite(customerSpaceId).pipe(
          mergeMap(() => [CoreActions.deleteCustomerSpaceFavoriteSucceeded({ customerSpaceId })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('fatal-error.text');
            return [CoreActions.deleteCustomerSpaceFavoriteFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  deleteFavorite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.deleteFavorite),
      switchMap(({ parentId }) =>
        this.favoriteService.deleteFavorite(parentId).pipe(
          mergeMap(() => [CoreActions.deleteFavoriteSucceeded()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('fatal-error.text');
            return [CoreActions.deleteFavoriteFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  loadCurrentUserRepresenteeIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadCurrentUserRepresenteeIds),
      switchMap(() =>
        this.userService.getCurrentUserRepresenteeIds().pipe(
          mergeMap((representeeUserIds) => [CoreActions.loadCurrentUserRepresenteeIdsSucceeded({ representeeIds: representeeUserIds })]),
          catchError((err: HttpErrorResponse) => [
            CoreActions.loadCurrentUserRepresenteeIdsFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadMobileUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.loadMobileUpload),
      switchMap(({ customerSpaceId, mobileUploadId }) =>
        this.mobileUploadService.getMobileUpload(customerSpaceId, mobileUploadId).pipe(
          mergeMap((mobileUploadDto) => [CoreActions.loadMobileUploadSucceeded({ mobileUploadDto })]),
          catchError((err: HttpErrorResponse) => [CoreActions.loadMobileUploadFailed({ status: err.status, errors: err.error })]),
        ),
      ),
    ),
  );

  uploadMobileFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.uploadMobileFiles),
      switchMap(({ customerSpaceId, mobileUploadId, fileList }) =>
        this.mobileUploadService.uploadFiles(customerSpaceId, mobileUploadId, fileList).pipe(
          tap(() => this.toastService.showSuccessToast('mobile-upload.upload.success-message')),
          mergeMap(() => [CoreActions.uploadMobileFilesSucceeded()]),
          catchError((err: HttpErrorResponse) => {
            if (err.status === HttpStatusCode.Gone) {
              return [
                go({ path: ['mobile-upload-expired'] }),
                CoreActions.uploadMobileFilesFailed({
                  status: err.status,
                  errors: err.error,
                }),
              ];
            }

            this.toastService.showErrorToast('mobile-upload.upload.error-message');
            return [
              CoreActions.uploadMobileFilesFailed({
                status: err.status,
                errors: err.error,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  uploadBatchCreationFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActions.uploadBatchCreationFile),
      switchMap(({ files }) =>
        this.customerSpaceService.uploadBatchCreationFiles(files).pipe(
          tap(() => this.toastService.showSuccessToast('admin.batch-creation.upload-success-message')),
          mergeMap(() => [CoreActions.uploadBatchCreationFileSucceeded()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('admin.batch-creation.upload-error-message');
            return [
              go({ path: ['admin'] }),
              CoreActions.uploadBatchCreationFileFailed({
                status: err.status,
                errors: err.error,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  uploadTermsUpdateCustomerSpaceIdsFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCustomerSpaceIsTermsUpdateGroup.uploadTermsUpdateCustomerSpacedIDsFile),
      switchMap(({ files }) =>
        this.customerSpaceService.uploadTermsUpdatesCustomerSpaceIdsFile(files).pipe(
          tap(() => this.toastService.showSuccessToast('admin.batch-creation.upload-success-message')),
          mergeMap(() => [uploadCustomerSpaceIsTermsUpdateApi.uploadTermsUpdateCustomerSpacedIDsFileSuccess()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('admin.batch-creation.upload-error-message');
            return [
              go({ path: ['admin'] }),
              uploadCustomerSpaceIsTermsUpdateApi.uploadTermsUpdateCustomerSpacedIDsFileFailed({
                status: err.status,
                errors: err.error,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private customerSpaceService: CustomerSpaceService,
    private userService: UserService,
    private toastService: ToastService,
    private sharepointService: SharepointService,
    private notificationService: NotificationService,
    private favoriteService: FavoriteService,
    private mobileUploadService: MobileUploadService,
    private store$: Store<RootState>,
  ) {}
}
